import Long from 'long';
import { IllegalArgumentException } from './lang/illegalargumentexception';

class EncodingSelector {
  private mCount: number;
  private mBits: number;
  private mMaxValue: Long;
  private mMaxValueN: number;
  private mIndex: number;

  public constructor(count: number, bits: number, maxValue: Long, index: number) {
    this.mCount = count;
    this.mBits = bits;
    this.mMaxValue = maxValue;
    this.mMaxValueN = maxValue.toNumber();
    this.mIndex = index;
  }

  public getCount(): number {
    return this.mCount;
  }

  public getBits(): number {
    return this.mBits;
  }

  public getMaxValue(): Long {
    return this.mMaxValue;
  }

  public getMaxValueN(): number {
    return this.mMaxValueN;
  }

  public getIndex(): number {
    return this.mIndex;
  }
}

class EncodingSelectors {
  public static kDummySelector = 0;
  public static kRLESelector = 1;
  public static kFirstSelector = 2; //			(* first regular selector value *)
  public static kMaxSelector = 15;

  public static mMaxCardinal = new Long(0xffffffff, 0x0fffffff, true);

  private constructor() {
    let encodingSelector = new EncodingSelector(0, 0, new Long(0, 0, true), 0);
    this.mEncodingSelectors.push(encodingSelector);
    encodingSelector = new EncodingSelector(0, 0, new Long(0, 0, true), 1);
    this.mEncodingSelectors.push(encodingSelector); //1
    encodingSelector = new EncodingSelector(60, 1, new Long(1, 0, true), 2);
    this.mEncodingSelectors.push(encodingSelector); //2
    encodingSelector = new EncodingSelector(30, 2, new Long(3, 0, true), 3);
    this.mEncodingSelectors.push(encodingSelector); // 3
    encodingSelector = new EncodingSelector(20, 3, new Long(0x7, 0, true), 4);
    this.mEncodingSelectors.push(encodingSelector); // 4
    encodingSelector = new EncodingSelector(15, 4, new Long(0xf, 0, true), 5);
    this.mEncodingSelectors.push(encodingSelector); // 5
    encodingSelector = new EncodingSelector(12, 5, new Long(0x1f, 0, true), 6);
    this.mEncodingSelectors.push(encodingSelector); // 6
    encodingSelector = new EncodingSelector(10, 6, new Long(0x3f, 0, true), 7);
    this.mEncodingSelectors.push(encodingSelector); // 7
    encodingSelector = new EncodingSelector(8, 7, new Long(0x7f, 0, true), 8);
    this.mEncodingSelectors.push(encodingSelector); // 8
    encodingSelector = new EncodingSelector(7, 8, new Long(0xff, 0, true), 9);
    this.mEncodingSelectors.push(encodingSelector); // 9
    encodingSelector = new EncodingSelector(6, 10, new Long(0x3ff, 0, true), 10);
    this.mEncodingSelectors.push(encodingSelector); //10
    encodingSelector = new EncodingSelector(5, 12, new Long(0xfff, 0, true), 11);
    this.mEncodingSelectors.push(encodingSelector); // 11
    encodingSelector = new EncodingSelector(4, 15, new Long(0x7fff, 0, true), 12);
    this.mEncodingSelectors.push(encodingSelector); // 12
    encodingSelector = new EncodingSelector(3, 20, new Long(0xfffff, 0, true), 13);
    this.mEncodingSelectors.push(encodingSelector); // 13
    encodingSelector = new EncodingSelector(2, 30, new Long(0x3fffffff, 0, true), 14);
    this.mEncodingSelectors.push(encodingSelector); // 14
    encodingSelector = new EncodingSelector(1, 60, EncodingSelectors.mMaxCardinal, 15);
    this.mEncodingSelectors.push(encodingSelector); // 15*/
  }
  private mEncodingSelectors = new Array<EncodingSelector>();

  public static getInstance(): EncodingSelectors {
    if (EncodingSelectors.s_Instance === null) {
      EncodingSelectors.s_Instance = new EncodingSelectors();
    }
    return EncodingSelectors.s_Instance;
  }

  public getSelectorByIndex(selector: number) {
    if (selector < 0 || selector > 15) {
      throw new IllegalArgumentException('Selector value must be between 0 and 15 inclusive: ' + selector);
    }
    return this.mEncodingSelectors[selector];
  }

  private static s_Instance: EncodingSelectors | null = null;
}

export class Simple8Compressor {
  public toNumberArray(arr: Uint8Array): Array<number> {
    const result = new Array<number>();
    for (let i = 0; i < 8; i++) {
      result[i] = arr[i];
    }
    return result;
  }

  public decompressSimple8(inData: DataView, inFrom: number, inCount: number): Array<number> {
    let index = 0;
    const encodingTables = EncodingSelectors.getInstance();
    let pos = inFrom + 4;
    let val;
    const result = new Array<number>();
    try {
      while (index < inCount) {
        const code1 = inData.getUint32(pos + 4, true);
        const code2 = inData.getUint32(pos, true);
        const selectorIndex = code1 >>> 28;

        if (selectorIndex === EncodingSelectors.kDummySelector) {
          return result;
        } else if (selectorIndex === EncodingSelectors.kRLESelector) {
          const count = (code1 << 4) >>> 4;
          for (let i = 0; i < count; i++) {
            result[index++] = code2;
          }
        } else if (selectorIndex === EncodingSelectors.kMaxSelector) {
          if (code1 << 4 > 0) {
            console.log('value too large to decode');
            result[index++] = 0;
          } else {
            result[index++] = code2;
          }
        } else {
          const selector = encodingTables.getSelectorByIndex(selectorIndex);
          const count = selector.getCount();
          const bits = selector.getBits();
          const max = selector.getMaxValueN();
          let shift = count * bits;

          for (let i = 0; i < count; i++) {
            shift -= bits;
            if (shift >= 32) {
              val = (code1 >>> (shift - 32)) & max;
            } else if (shift + bits <= 32) {
              val = (code2 >>> shift) & max;
            } else {
              const val1 = (code1 << (32 - shift)) & max;
              const val2 = code2 >>> shift;
              val = val1 | val2;
            }
            result[index++] = val;
          }
        }
        pos += 8;
      }
    } catch (error) {
      console.log(error);
      /*
			console.log('inFrom:' + inFrom);
			console.log('inCount:' + inCount);
			console.log('nrBytes:' + nrBytes);
			console.log('pos:' + pos);
			console.log('index:' + index);
			*/
    }
    return result;
  }

  private cardToInt32(inVal: number): number {
    let val = inVal;
    const odd = val % 2 === 1;
    if (odd) {
      val++;
    }
    val = val >>> 1 || val << 31;
    if (odd) {
      val = -val;
    }
    return val;
  }

  public decompressCardinal8(inData: DataView, inFrom: number, inCount: number): Uint8Array {
    const decompressed = this.decompressSimple8(inData, inFrom, inCount);
    const result = new Uint8Array(decompressed.length);
    for (let i = 0; i < decompressed.length; i++) {
      result[i] = decompressed[i];
    }
    return result;
  }

  public decompressCardinal8Delta(inData: DataView, inFrom: number, inCount: number): Uint8Array {
    const decompressed = this.decompressSimple8(inData, inFrom, inCount);
    const result = new Uint8Array(decompressed.length);
    result[0] = this.cardToInt32(decompressed[0]);
    for (let i = 1; i < decompressed.length; i++) {
      const delta = this.cardToInt32(decompressed[i]);
      result[i] = result[i - 1] + delta;
    }
    return result;
  }

  public decompressCardinal32Delta(inData: DataView, inFrom: number, inCount: number): Uint32Array {
    const decompressed = this.decompressSimple8(inData, inFrom, inCount);
    const result = new Uint32Array(decompressed.length);
    result[0] = this.cardToInt32(decompressed[0]);
    for (let i = 1; i < decompressed.length; i++) {
      const delta = this.cardToInt32(decompressed[i]);
      result[i] = result[i - 1] + delta;
    }
    return result;
  }

  public decompressInteger32Delta(inData: DataView, inFrom: number, inCount: number): Int32Array {
    const decompressed = this.decompressSimple8(inData, inFrom, inCount);
    const result = new Int32Array(decompressed.length);
    let value = this.cardToInt32(decompressed[0]);
    result[0] = value;
    for (let i = 1; i < decompressed.length; i++) {
      const delta = this.cardToInt32(decompressed[i]);
      value += delta;
      result[i] = value;
    }
    return result;
  }
}
